import { useState, type CSSProperties, type ReactNode } from "react";
import { healthToneKey } from "../health";
import {
  incidentIdForRemediation,
  remediationActionsForComponent,
  type RemediationAction,
} from "../remediationActions";
import {
  isVisibleByDefault,
  presenceDisplayLabel,
  type RuntimeHealthComponent,
  type RuntimeHealthDomain,
} from "../runtimeDomains";
import type { HealthLabel, NotMonitoredLabel } from "../types";
import {
  evidenceQualityBadge,
  formatVerificationAge,
} from "../verification";
import {
  operatorFacingMessage,
  shortOperatorRecommendation,
} from "./operatorCopy";

/** Component's display text — presence-aware (Not Installed / Unavailable) or health label. */
function componentDisplayText(component: RuntimeHealthComponent): string {
  if (component.presence === "not_installed" || component.presence === "unavailable") {
    return presenceDisplayLabel(component);
  }
  return component.verificationLabel ?? component.label;
}

const TONE_COLOR: Record<ReturnType<typeof healthToneKey>, string> = {
  success: "#6ee7b7",
  warning: "#fcd34d",
  danger: "#fda4af",
  neutral: "#94a3b8",
};

function labelColor(label: HealthLabel | NotMonitoredLabel): string {
  return TONE_COLOR[healthToneKey(label as HealthLabel)] ?? TONE_COLOR.neutral;
}

export type RuntimeHealthExecuteAction = (input: {
  incidentId: string;
  actionId: string;
  componentId: string;
  confirmed?: boolean;
  params?: Record<string, string>;
}) => void | Promise<void>;

export type RuntimeHealthDomainsPanelProps = {
  domains: RuntimeHealthDomain[];
  /**
   * ops — compact domain + component cards.
   * controlPlane — same cards with unavailable toggle.
   */
  surface: "ops" | "controlPlane";
  className?: string;
  style?: CSSProperties;
  /** Tenant slug for resolving `{slug}:{check}` incident ids. */
  tenantSlug?: string | null;
  /** Live attention / incident ids for this tenant (and host). */
  attentionIds?: string[];
  /** Execute an existing platform incident action (no new APIs). */
  onExecuteAction?: RuntimeHealthExecuteAction;
  /** Busy key `${incidentId}:${actionId}` while an action runs. */
  actionBusyKey?: string | null;
};

/**
 * Runtime Health by operational domain (Platform / Tenant Runtime /
 * Integrations / Infrastructure). Shared Ops + Control Plane chrome.
 * Level 2 compact cards by default; diagnostics behind Details.
 */
export function RuntimeHealthDomainsPanel({
  domains,
  surface,
  className = "",
  style,
  tenantSlug = null,
  attentionIds = [],
  onExecuteAction,
  actionBusyKey = null,
}: RuntimeHealthDomainsPanelProps): ReactNode {
  const showUnavailableToggle = surface === "controlPlane";
  const [showUnavailable, setShowUnavailable] = useState(false);
  const hiddenCount = domains.reduce(
    (sum, d) => sum + d.components.filter((c) => !isVisibleByDefault(c)).length,
    0,
  );

  return (
    <div
      className={`runtime-health-domains runtime-health-domains--${surface} ${className}`.trim()}
      style={style}
      data-surface={surface}
    >
      {showUnavailableToggle && hiddenCount > 0 ? (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            fontSize: "0.72rem",
            color: "#94a3b8",
            marginBottom: "0.6rem",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showUnavailable}
            onChange={(e) => setShowUnavailable(e.target.checked)}
          />
          Show unavailable components ({hiddenCount})
        </label>
      ) : null}
      <div
        style={{
          display: "grid",
          gap: "0.75rem",
          gridTemplateColumns: "1fr",
        }}
      >
        {domains.map((domain) => (
          <DomainBlock
            key={domain.id}
            domain={domain}
            showUnavailable={showUnavailableToggle ? showUnavailable : false}
            compactList={surface === "ops"}
            tenantSlug={tenantSlug}
            attentionIds={attentionIds}
            onExecuteAction={onExecuteAction}
            actionBusyKey={actionBusyKey}
          />
        ))}
      </div>
    </div>
  );
}

function DomainBlock({
  domain,
  showUnavailable,
  compactList,
  tenantSlug,
  attentionIds,
  onExecuteAction,
  actionBusyKey,
}: {
  domain: RuntimeHealthDomain;
  showUnavailable: boolean;
  /** Ops: collapsed domain with short component list until expanded. */
  compactList: boolean;
  tenantSlug?: string | null;
  attentionIds: string[];
  onExecuteAction?: RuntimeHealthExecuteAction;
  actionBusyKey?: string | null;
}): ReactNode {
  const [domainOpen, setDomainOpen] = useState(!compactList);
  const color = labelColor(domain.summary);
  const visibleComponents = showUnavailable
    ? domain.components
    : domain.components.filter(isVisibleByDefault);
  const hiddenInDomain = domain.components.length - visibleComponents.length;

  const summaryLine = (
    <>
      <strong style={{ color }}>{domain.summaryDisplay}</strong>
      {domain.freshness ? (
        <span style={{ color: "#64748b" }}>{` · ${evidenceQualityBadge(domain.freshness)}`}</span>
      ) : null}
      <span style={{ display: "block", marginTop: "0.2rem", color: "#94a3b8", fontSize: "0.72rem" }}>
        {domain.impactSummary}
      </span>
    </>
  );

  return (
    <section
      className={`runtime-health-domain runtime-health-domain--${domain.id}`}
      data-domain={domain.id}
      data-summary={domain.summary}
      style={{
        border: "1px solid #1e293b",
        borderRadius: "0.5rem",
        background: "rgba(2, 6, 23, 0.35)",
        padding: "0.75rem",
      }}
    >
      <button
        type="button"
        onClick={() => setDomainOpen((v) => !v)}
        aria-expanded={domainOpen}
        style={{
          display: "flex",
          width: "100%",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.5rem",
          margin: 0,
          padding: 0,
          border: "none",
          background: "transparent",
          color: "inherit",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            <span aria-hidden="true" style={{ marginRight: "0.35rem" }}>
              {domainOpen ? "▼" : "▶"}
            </span>
            {domain.name}
          </p>
          {!domainOpen ? (
            <div style={{ marginTop: "0.35rem", fontSize: "0.8rem", color: "#cbd5e1" }}>
              {summaryLine}
            </div>
          ) : (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.72rem", color: "#64748b" }}>
              {domain.question}
            </p>
          )}
        </div>
        {domainOpen ? (
          <div style={{ textAlign: "right" }}>
            <strong style={{ color, fontSize: "0.9rem" }}>{domain.summaryDisplay}</strong>
            {domain.freshness ? (
              <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                {evidenceQualityBadge(domain.freshness)}
              </p>
            ) : null}
          </div>
        ) : null}
      </button>

      {domainOpen ? (
        <>
          <p style={{ margin: "0.45rem 0 0", fontSize: "0.78rem", color: "#cbd5e1" }}>
            {domain.impactSummary}
          </p>
          <div
            style={{
              marginTop: "0.65rem",
              display: "grid",
              gap: "0.5rem",
              gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))",
            }}
          >
            {visibleComponents.map((c) => (
              <ComponentCard
                key={c.id}
                component={c}
                tenantSlug={tenantSlug}
                attentionIds={attentionIds}
                onExecuteAction={onExecuteAction}
                actionBusyKey={actionBusyKey}
              />
            ))}
            {hiddenInDomain > 0 ? (
              <p style={{ margin: 0, fontSize: "0.68rem", color: "#64748b" }}>
                +{hiddenInDomain} not installed / unavailable
                {showUnavailable ? "" : " — enable toggle to show"}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}

function ComponentCard({
  component,
  tenantSlug,
  attentionIds,
  onExecuteAction,
  actionBusyKey,
}: {
  component: RuntimeHealthComponent;
  tenantSlug?: string | null;
  attentionIds: string[];
  onExecuteAction?: RuntimeHealthExecuteAction;
  actionBusyKey?: string | null;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const color = labelColor(component.label);
  const confidence =
    component.verifications.find((v) => v.confidence)?.confidence ?? null;
  const verifiedAge = formatVerificationAge(component.lastVerification);
  const lastVerified = verifiedAge ? `Verified ${verifiedAge}` : null;
  const evidenceQuality = component.verificationFreshness
    ? evidenceQualityBadge(component.verificationFreshness)
    : null;
  const reason = operatorFacingMessage(component.reason);
  const recommendation = shortOperatorRecommendation(component.recommendation);
  const explanation = operatorFacingMessage(component.explanation);
  const remediations = remediationActionsForComponent(component);
  const canExecute = Boolean(onExecuteAction && tenantSlug);
  const hasDetails =
    Boolean(reason) ||
    Boolean(component.evidence) ||
    Boolean(recommendation) ||
    Boolean(explanation) ||
    component.verifications.length > 0 ||
    Boolean(component.verificationMethod) ||
    component.durationMs != null;

  const runAction = (action: RemediationAction) => {
    if (!onExecuteAction || !tenantSlug) return;
    if (action.requiresConfirmation) {
      const ok = window.confirm(
        `${action.label} requires confirmation (${action.risk ?? "elevated"} risk` +
          (action.estimatedDurationSeconds
            ? `, ~${Math.round(action.estimatedDurationSeconds / 60)} min`
            : "") +
          "). Continue?",
      );
      if (!ok) return;
    }
    const incidentId = incidentIdForRemediation({
      actionId: action.id,
      tenantSlug,
      attentionIds,
      componentId: component.id,
    });
    void onExecuteAction({
      incidentId,
      actionId: action.id,
      componentId: component.id,
      confirmed: Boolean(action.requiresConfirmation) || action.automationLevel !== "confirm",
      params: action.params,
    });
  };

  return (
    <div
      data-component={component.id}
      data-domain={component.domain}
      style={{
        border: "1px solid #1e293b",
        borderRadius: "0.4rem",
        background: "rgba(15, 23, 42, 0.5)",
        padding: "0.55rem 0.65rem",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "0.68rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "#94a3b8",
        }}
      >
        {component.name}
      </p>
      <p style={{ margin: "0.3rem 0 0" }}>
        <span style={{ color, fontSize: "0.85rem", fontWeight: 600 }}>
          {componentDisplayText(component)}
        </span>
      </p>
      {lastVerified ? (
        <p style={{ margin: "0.2rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
          {lastVerified}
        </p>
      ) : null}
      {evidenceQuality ? (
        <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
          {evidenceQuality}
        </p>
      ) : null}
      {confidence ? (
        <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
          Confidence {confidence}
        </p>
      ) : null}

      {canExecute && remediations.length > 0 ? (
        <div
          style={{
            marginTop: "0.4rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.35rem",
          }}
        >
          {remediations.map((action) => {
            const incidentId = incidentIdForRemediation({
              actionId: action.id,
              tenantSlug: tenantSlug!,
              attentionIds,
              componentId: component.id,
            });
            const busyKey = `${incidentId}:${action.id}`;
            const busy = actionBusyKey === busyKey;
            return (
              <button
                key={action.id}
                type="button"
                disabled={Boolean(actionBusyKey)}
                onClick={() => runAction(action)}
                style={{
                  padding: "0.28rem 0.55rem",
                  borderRadius: "0.3rem",
                  border: action.destructive
                    ? "1px solid #7f1d1d"
                    : action.primary
                      ? "1px solid #1d4ed8"
                      : "1px solid #334155",
                  background: action.destructive
                    ? "rgba(127, 29, 29, 0.35)"
                    : action.primary
                      ? "rgba(37, 99, 235, 0.35)"
                      : "rgba(30, 41, 59, 0.8)",
                  color: action.destructive ? "#fecaca" : "#e2e8f0",
                  fontSize: "0.68rem",
                  fontWeight: action.primary ? 600 : 500,
                  cursor: actionBusyKey ? "not-allowed" : "pointer",
                  opacity: actionBusyKey && !busy ? 0.55 : 1,
                }}
              >
                {busy
                  ? "Running…"
                  : `${action.label}${
                      action.automationLevel === "confirm"
                        ? " · Confirm"
                        : action.estimatedDurationSeconds
                          ? ` · ~${Math.max(1, Math.round(action.estimatedDurationSeconds / 60))}m`
                          : ""
                    }`}
              </button>
            );
          })}
        </div>
      ) : !open && recommendation ? (
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.72rem", color: "#fcd34d" }}>
          {recommendation}
        </p>
      ) : null}

      {hasDetails ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: "0.35rem",
            padding: 0,
            border: "none",
            background: "transparent",
            color: "#93c5fd",
            fontSize: "0.68rem",
            cursor: "pointer",
          }}
        >
          {open ? "Hide details" : "▶ Details"}
        </button>
      ) : null}

      {open ? (
        <div style={{ marginTop: "0.4rem" }}>
          {reason ? (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.72rem", color: "#cbd5e1" }}>
              <span style={{ color: "#94a3b8" }}>Reason: </span>
              {reason}
            </p>
          ) : null}
          {explanation ? (
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.7rem", color: "#64748b" }}>
              {explanation}
            </p>
          ) : null}
          {recommendation && remediations.length === 0 ? (
            <p style={{ margin: "0.25rem 0 0", fontSize: "0.7rem", color: "#fcd34d" }}>
              <span style={{ color: "#94a3b8" }}>Recommendation: </span>
              {recommendation}
            </p>
          ) : null}
          {recommendation && remediations.length > 0 ? (
            <p style={{ margin: "0.25rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
              {recommendation}
            </p>
          ) : null}

          <details style={{ marginTop: "0.45rem" }}>
            <summary
              style={{
                cursor: "pointer",
                fontSize: "0.68rem",
                color: "#93c5fd",
                listStyle: "none",
              }}
            >
              ▶ Diagnostics
            </summary>
            <div style={{ marginTop: "0.35rem" }}>
              {component.verificationMethod ? (
                <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                  Method: {component.verificationMethod}
                  {component.durationMs != null ? ` · ${component.durationMs} ms` : ""}
                </p>
              ) : null}
              {component.lastVerification ? (
                <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                  Timestamp: {component.lastVerification}
                </p>
              ) : null}
              {component.evidence ? (
                <p style={{ margin: "0.2rem 0 0", fontSize: "0.7rem", color: "#94a3b8" }}>
                  Evidence: {operatorFacingMessage(component.evidence) ?? component.evidence}
                </p>
              ) : null}
              {component.verifications.map((v) => (
                <div
                  key={`${v.component}-${v.timestamp}`}
                  style={{
                    marginTop: "0.35rem",
                    paddingTop: "0.35rem",
                    borderTop: "1px solid #1e293b",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.68rem", color: "#94a3b8" }}>
                    {v.component} · {v.status} · {v.verification_method} · {v.timestamp}
                  </p>
                  {v.endpoint ? (
                    <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                      Endpoint: {v.endpoint}
                    </p>
                  ) : null}
                  {v.confidence || v.freshness || v.duration_ms != null ? (
                    <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                      {[
                        v.confidence ? `confidence ${v.confidence}` : null,
                        v.freshness ? evidenceQualityBadge(v.freshness) : null,
                        v.duration_ms != null ? `${v.duration_ms} ms` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}
                  {v.deployment_id || v.incident_id || v.request_id || v.trace_id ? (
                    <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                      Correlated:{" "}
                      {[
                        v.deployment_id ? `deployment ${v.deployment_id}` : null,
                        v.incident_id ? `incident ${v.incident_id}` : null,
                        v.request_id ? `request ${v.request_id}` : null,
                        v.trace_id ? `trace ${v.trace_id}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}
                  <ul
                    style={{
                      margin: "0.25rem 0 0",
                      paddingLeft: "1rem",
                      fontSize: "0.68rem",
                      color: "#cbd5e1",
                    }}
                  >
                    {v.evidence.map((e) => (
                      <li key={`${e.label}-${e.value}`}>
                        <span style={{ color: "#94a3b8" }}>{e.label}: </span>
                        {operatorFacingMessage(e.value) ?? e.value}
                      </li>
                    ))}
                  </ul>
                  {v.recommendation ? (
                    <p style={{ margin: "0.2rem 0 0", fontSize: "0.68rem", color: "#fcd34d" }}>
                      Recommendation:{" "}
                      {shortOperatorRecommendation(v.recommendation) ?? v.recommendation}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </details>
        </div>
      ) : null}
    </div>
  );
}
